import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/posts/${slug}`);
        setPost(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="h-6 w-24 bg-brand-bg rounded animate-pulse" />
        <div className="mt-6 h-10 w-3/4 bg-brand-bg rounded animate-pulse" />
        <div className="mt-10 aspect-[16/9] rounded-3xl bg-brand-bg animate-pulse" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div data-testid="blog-post-not-found" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h1 className="font-display text-3xl text-brand-ink">Post not found.</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-brand-sageDark">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </div>
    );
  }

  return (
    <article data-testid="blog-post-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        category={post.category}
        publishedAt={post.published_at}
      />
      <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-brand-inkMuted hover:text-brand-ink transition-colors">
        <ArrowLeft className="h-4 w-4" /> All articles
      </Link>

      <div className="mt-8 flex items-center gap-3 text-xs">
        <span className="px-2.5 py-1 rounded-full bg-brand-sage/10 text-brand-sageDark font-medium">
          {post.category}
        </span>
        <span className="inline-flex items-center gap-1 text-brand-inkMuted">
          <Clock className="h-3 w-3" /> {post.read_minutes} min read
        </span>
      </div>

      <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-brand-ink leading-[1.1]">
        {post.title}
      </h1>

      <p className="mt-5 text-lg text-brand-inkSoft leading-relaxed">{post.excerpt}</p>

      <div className="mt-10 rounded-3xl overflow-hidden">
        <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover" />
      </div>

      <div className="mt-10 max-w-none">
        {post.body.split("\n\n").map((block, i) => {
          if (block.startsWith("## ")) {
            return (
              <h2
                key={i}
                className="font-display text-2xl sm:text-3xl font-medium text-brand-ink tracking-tight mt-10 mb-3"
              >
                {block.replace(/^##\s+/, "")}
              </h2>
            );
          }
          return (
            <p
              key={i}
              className="text-brand-inkSoft leading-relaxed text-base sm:text-lg mb-5 whitespace-pre-line"
            >
              {block}
            </p>
          );
        })}
      </div>

      <div className="mt-16 rounded-2xl bg-brand-bg border border-brand-border p-8 text-center">
        <h3 className="font-display text-xl text-brand-ink">Like this? Get one in your inbox each week.</h3>
        <Link
          to="/resources#subscribe"
          className="mt-4 inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-6 py-3 rounded-full font-medium"
        >
          Subscribe to the newsletter
        </Link>
      </div>
    </article>
  );
}
